import { SimpleEventEmitter } from "./utils/simple-event-emitter.mjs";
import { isArray, isUndefined } from "@posthog/core";
import { matchPropertyFilters } from "@posthog/core/surveys";
import { extractHref, extractTexts, matchString, matchTexts } from "./utils/elements-chain-utils.mjs";
class ActionMatcher {
    constructor(_host){
        this._host = _host;
        this._actionRegistry = new Set();
        this._actionEvents = new Set();
        this._debugEventEmitter = new SimpleEventEmitter();
        this._checkStep = (event, step)=>this._checkStepEvent(event, step) && this._checkStepUrl(event, step) && this._checkStepElement(event, step) && this._checkStepProperties(event, step);
        this._checkStepEvent = (event, step)=>{
            if (step?.event && event?.event !== step?.event) return false;
            return true;
        };
    }
    init() {
        if (!isUndefined(this._host?.subscribeCapture)) {
            const matchEventToAction = (eventName, eventPayload)=>{
                this.on(eventName, eventPayload);
            };
            this._captureHookUnsubscribe = this._host?.subscribeCapture(matchEventToAction);
        }
    }
    dispose() {
        this._captureHookUnsubscribe?.();
        this._captureHookUnsubscribe = void 0;
        this._host?.setElementSelectors?.(new Set(), this);
        this._debugEventEmitter = new SimpleEventEmitter();
    }
    register(actions) {
        if (isUndefined(this._host?.subscribeCapture)) return;
        actions.forEach((action)=>{
            this._actionRegistry.add(action);
            action.steps?.forEach((step)=>{
                this._actionEvents.add(step?.event || '');
            });
        });
        if (this._host?.setElementSelectors) {
            const selectorsToWatch = new Set();
            this._actionRegistry.forEach((action)=>{
                action.steps?.forEach((step)=>{
                    if (step?.selector) selectorsToWatch.add(step.selector);
                });
            });
            this._host.setElementSelectors(selectorsToWatch, this);
        }
    }
    replace(actions) {
        this._actionRegistry.clear();
        this._actionEvents.clear();
        this.register(actions);
    }
    on(eventName, eventPayload) {
        if (null == eventPayload || 0 == eventName.length) return;
        if (!this._actionEvents.has(eventName) && !this._actionEvents.has(eventPayload.event)) return;
        this._actionRegistry.forEach((action)=>{
            if (this._checkAction(eventPayload, action)) this._debugEventEmitter.emit('actionCaptured', action.name);
        });
    }
    _addActionHook(callback) {
        this.onAction('actionCaptured', (data)=>callback(data));
    }
    _checkAction(event, action) {
        if (action?.steps == null) return false;
        for (const step of action.steps)if (this._checkStep(event, step)) return true;
        return false;
    }
    onAction(event, cb) {
        return this._debugEventEmitter.on(event, cb);
    }
    _checkStepUrl(event, step) {
        if (step?.url) {
            let eventUrl = event?.properties?.$current_url;
            if (!event?.properties || !('$current_url' in event.properties)) try {
                eventUrl = this._host?.getActionUrl?.();
            } catch  {
                return false;
            }
            if (!eventUrl || 'string' != typeof eventUrl) return false;
            if (!matchString(eventUrl, step.url, step.url_matching || 'contains')) return false;
        }
        return true;
    }
    _checkStepElement(event, step) {
        if (!this._checkStepHref(event, step)) return false;
        if (!this._checkStepText(event, step)) return false;
        if (!this._checkStepSelector(event, step)) return false;
        return true;
    }
    _checkStepHref(event, step) {
        if (!step?.href) return true;
        const elements = this._getElementsList(event);
        if (elements.length > 0) return elements.some((el)=>matchString(el.href, step.href, step.href_matching || 'exact'));
        const chain = event?.properties?.$elements_chain || '';
        if (chain) return matchString(extractHref(chain), step.href, step.href_matching || 'exact');
        return false;
    }
    _checkStepText(event, step) {
        if (!step?.text) return true;
        const elements = this._getElementsList(event);
        if (elements.length > 0) return elements.some((el)=>matchString(el.text, step.text, step.text_matching || 'exact') || matchString(el.$el_text, step.text, step.text_matching || 'exact'));
        const chain = event?.properties?.$elements_chain || '';
        if (chain) return matchTexts(extractTexts(chain), step.text, step.text_matching || 'exact');
        return false;
    }
    _checkStepSelector(event, step) {
        if (!step?.selector) return true;
        const elementSelectors = event?.properties?.$element_selectors;
        if (elementSelectors?.includes(step.selector)) return true;
        const chain = event?.properties?.$elements_chain || '';
        if (step.selector_regex && chain) try {
            return new RegExp(step.selector_regex).test(chain);
        } catch  {}
        return false;
    }
    _getElementsList(event) {
        if (event?.properties?.$elements == null) return [];
        return event?.properties.$elements;
    }
    _checkStepProperties(event, step) {
        if (!step?.properties || 0 === step.properties.length) return true;
        const propertyFilters = step.properties.reduce((acc, filter)=>{
            const values = isArray(filter.value) ? filter.value.map(String) : null != filter.value ? [
                String(filter.value)
            ] : [];
            acc[filter.key] = {
                values,
                operator: filter.operator || 'exact'
            };
            return acc;
        }, {});
        return matchPropertyFilters(propertyFilters, event?.properties);
    }
}
export { ActionMatcher };
